import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    MessageSquare,
    CheckCircle2,
    FileText,
    Clock,
    AlertCircle,
    Shield,
    Zap,
    Filter
} from 'lucide-react';

type TimeFilter = 'all' | '1week' | '2weeks' | '1month' | '3months' | '6months';

// Mock data - structured to be easily replaceable with real data
// In a real implementation, this would be fetched based on the time filter
const getAllTimeData = () => ({
    overview: {
        totalQuestions: 1123,
        autoResolvedPercentage: 65,
        draftsCreated: 280,
        offHoursQuestions: 180, // before + after business hours
    },
    availability: {
        highConfidenceAnswers: 660,
        resolvedWithoutReview: 73,
        beforeBusinessHours: 85,
        businessHours: 1020,
        afterBusinessHours: 95,
    },
    contextSwitching: {
        strategicEscalations: 420,
        draftsGenerated: 280,
        escalationsWithDraft: 196,
        draftsApprovedWithoutInstructions: 147, // ~75% of drafts with draft
    },
    confidence: {
        high: 660, // 55%
        medium: 360, // 30%
        low: 180, // 15%
        autoAnswered: 780,
        escalated: 420,
        blocked: 0,
    },
    guardrails: {
        triggered: 45,
        blockedAnswers: 12,
        escalatedDueToGuardrails: 33,
    },
    trends: {
        weeklyQuestions: [
            { week: 'Week 1', total: 180, autoResolved: 117, escalated: 63 },
            { week: 'Week 2', total: 210, autoResolved: 136, escalated: 74 },
            { week: 'Week 3', total: 195, autoResolved: 127, escalated: 68 },
            { week: 'Week 4', total: 225, autoResolved: 146, escalated: 79 },
            { week: 'Week 5', total: 240, autoResolved: 156, escalated: 84 },
            { week: 'Week 6', total: 150, autoResolved: 98, escalated: 52 },
        ],
    },
});

// Helper function to get filtered data based on time period
const getFilteredData = (timeFilter: TimeFilter) => {
    const allData = getAllTimeData();

    if (timeFilter === 'all') {
        return allData;
    }

    // Calculate the number of weeks to include
    let weeksToInclude = 0;
    switch (timeFilter) {
        case '1week':
            weeksToInclude = 1;
            break;
        case '2weeks':
            weeksToInclude = 2;
            break;
        case '1month':
            weeksToInclude = 4;
            break;
        case '3months':
            weeksToInclude = 12;
            break;
        case '6months':
            weeksToInclude = 24;
            break;
        default:
            weeksToInclude = allData.trends.weeklyQuestions.length;
    }

    // Get the most recent N weeks
    const filteredWeeks = allData.trends.weeklyQuestions.slice(-weeksToInclude);

    // Calculate totals from filtered weeks
    const totalQuestions = filteredWeeks.reduce((sum, week) => sum + week.total, 0);
    const totalAutoResolved = filteredWeeks.reduce((sum, week) => sum + week.autoResolved, 0);
    const totalEscalated = filteredWeeks.reduce((sum, week) => sum + week.escalated, 0);

    // Scale other metrics proportionally (in real app, these would be calculated from actual filtered data)
    const scaleFactor = totalQuestions / allData.overview.totalQuestions;

    return {
        overview: {
            totalQuestions,
            autoResolvedPercentage: totalQuestions > 0 ? Math.round((totalAutoResolved / totalQuestions) * 100) : 0,
            draftsCreated: Math.round(allData.overview.draftsCreated * scaleFactor),
            offHoursQuestions: Math.round(allData.overview.offHoursQuestions * scaleFactor),
        },
        availability: {
            highConfidenceAnswers: Math.round(allData.availability.highConfidenceAnswers * scaleFactor),
            resolvedWithoutReview: Math.round(allData.availability.resolvedWithoutReview * scaleFactor),
            beforeBusinessHours: Math.round(allData.availability.beforeBusinessHours * scaleFactor),
            businessHours: Math.round(allData.availability.businessHours * scaleFactor),
            afterBusinessHours: Math.round(allData.availability.afterBusinessHours * scaleFactor),
        },
        contextSwitching: {
            strategicEscalations: totalEscalated,
            draftsGenerated: Math.round(allData.contextSwitching.draftsGenerated * scaleFactor),
            escalationsWithDraft: Math.round(allData.contextSwitching.escalationsWithDraft * scaleFactor),
            draftsApprovedWithoutInstructions: Math.round(allData.contextSwitching.draftsApprovedWithoutInstructions * scaleFactor),
        },
        confidence: {
            high: Math.round(allData.confidence.high * scaleFactor),
            medium: Math.round(allData.confidence.medium * scaleFactor),
            low: Math.round(allData.confidence.low * scaleFactor),
            autoAnswered: totalAutoResolved,
            escalated: totalEscalated,
            blocked: Math.round(allData.confidence.blocked * scaleFactor),
        },
        guardrails: {
            triggered: Math.round(allData.guardrails.triggered * scaleFactor),
            blockedAnswers: Math.round(allData.guardrails.blockedAnswers * scaleFactor),
            escalatedDueToGuardrails: Math.round(allData.guardrails.escalatedDueToGuardrails * scaleFactor),
        },
        trends: {
            weeklyQuestions: filteredWeeks,
        },
    };
};

export const AnalyticsPage: React.FC = () => {
    const [timeFilter, setTimeFilter] = useState<TimeFilter>('all');

    // Get filtered data based on selected time period
    const filteredData = useMemo(() => getFilteredData(timeFilter), [timeFilter]);
    const { overview, availability, contextSwitching, confidence, trends, guardrails } = filteredData;

    // Calculate max value for trend chart scaling
    const maxTrendValue = Math.max(
        ...trends.weeklyQuestions.flatMap(w => [w.total, w.autoResolved, w.escalated]),
        1 // Ensure at least 1 to avoid division by zero
    );

    return (
        <div className="flex flex-col h-screen overflow-auto bg-background">
            <div className="p-6 border-b bg-background">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-semibold mb-2">Analytics</h1>
                        <p className="text-sm text-muted-foreground">
                            Value & Impact Dashboard - Understanding work handled and focus protected
                        </p>
                    </div>
                </div>

                {/* Time Filter */}
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-muted-foreground">Time Period:</span>
                    </div>
                    <Select value={timeFilter} onValueChange={(value) => setTimeFilter(value as TimeFilter)}>
                        <SelectTrigger className="w-[180px] h-9">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Time</SelectItem>
                            <SelectItem value="1week">Past 1 Week</SelectItem>
                            <SelectItem value="2weeks">Past 2 Weeks</SelectItem>
                            <SelectItem value="1month">Past 1 Month</SelectItem>
                            <SelectItem value="3months">Past 3 Months</SelectItem>
                            <SelectItem value="6months">Past 6 Months</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="flex-1 overflow-auto p-6">
                <div className="max-w-7xl mx-auto space-y-8">
                    {/* Overview Section */}
                    <section>
                        <h2 className="text-xl font-semibold mb-4">Overview</h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardDescription>Total Questions</CardDescription>
                                    <CardTitle className="text-3xl font-bold">{overview.totalQuestions.toLocaleString()}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <MessageSquare className="h-4 w-4" />
                                        <span>All time</span>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-3">
                                    <CardDescription>Auto-Resolved</CardDescription>
                                    <CardTitle className="text-3xl font-bold">{overview.autoResolvedPercentage}%</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                                        <span>Without human involvement</span>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-3">
                                    <CardDescription>Drafts Created</CardDescription>
                                    <CardTitle className="text-3xl font-bold">{overview.draftsCreated.toLocaleString()}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                        <span>For human review</span>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader className="pb-3">
                                    <CardDescription>Off-Hours Questions</CardDescription>
                                    <CardTitle className="text-3xl font-bold">{overview.offHoursQuestions.toLocaleString()}</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <Clock className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                                        <span>Outside business hours</span>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </section>

                    {/* 24/7 Availability Section */}
                    <section>
                        <h2 className="text-xl font-semibold mb-4">People unblocked</h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            Questions answered confidently without escalation, demonstrating round-the-clock availability
                        </p>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Automation Metrics</CardTitle>
                                    <CardDescription>Questions handled without human involvement</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-full bg-green-100 dark:bg-green-900/20 flex items-center justify-center">
                                                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium">High-Confidence Answers</p>
                                                <p className="text-xs text-muted-foreground">Auto-answered with high confidence</p>
                                            </div>
                                        </div>
                                        <div className="text-2xl font-bold">{availability.highConfidenceAnswers.toLocaleString()}</div>
                                    </div>

                                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                                                <Zap className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-medium">Blocked because of low confidence</p>
                                                <p className="text-xs text-muted-foreground">No human intervention required</p>
                                            </div>
                                        </div>
                                        <div className="text-2xl font-bold">{availability.resolvedWithoutReview.toLocaleString()}</div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Time Distribution</CardTitle>
                                    <CardDescription>Questions by time period</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium">Before Business Hours</span>
                                                <span className="text-sm font-bold">{availability.beforeBusinessHours.toLocaleString()}</span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-3">
                                                <div
                                                    className="bg-purple-600 h-3 rounded-full"
                                                    style={{ width: `${(availability.beforeBusinessHours / (availability.beforeBusinessHours + availability.businessHours + availability.afterBusinessHours)) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium">Business Hours</span>
                                                <span className="text-sm font-bold">{availability.businessHours.toLocaleString()}</span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-3">
                                                <div
                                                    className="bg-blue-600 h-3 rounded-full"
                                                    style={{ width: `${(availability.businessHours / (availability.beforeBusinessHours + availability.businessHours + availability.afterBusinessHours)) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-sm font-medium">After Business Hours</span>
                                                <span className="text-sm font-bold">{availability.afterBusinessHours.toLocaleString()}</span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-3">
                                                <div
                                                    className="bg-orange-600 h-3 rounded-full"
                                                    style={{ width: `${(availability.afterBusinessHours / (availability.beforeBusinessHours + availability.businessHours + availability.afterBusinessHours)) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-4">
                                            {availability.beforeBusinessHours + availability.afterBusinessHours} questions ({Math.round(((availability.beforeBusinessHours + availability.afterBusinessHours) / (availability.beforeBusinessHours + availability.businessHours + availability.afterBusinessHours)) * 100)}%) were handled outside business hours
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </section>

                    {/* Context Switching Avoided Section */}
                    <section>
                        <h2 className="text-xl font-semibold mb-4">Context Switching Avoided</h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            Interruptions absorbed by the system - humans receive prepared context, not blank slates
                        </p>
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Answers Given on My Behalf</CardTitle>
                                    <CardDescription>How many answers it gave on my behalf</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold mb-2">{confidence.autoAnswered.toLocaleString()}</div>
                                    <p className="text-sm text-muted-foreground">
                                        Automatically answered without your involvement
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Drafts Generated</CardTitle>
                                    <CardDescription>Prepared before human involvement</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold mb-2">{contextSwitching.draftsGenerated.toLocaleString()}</div>
                                    <p className="text-sm text-muted-foreground">
                                        Structured responses ready for review
                                    </p>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Drafts Approved Without Instructions</CardTitle>
                                    <CardDescription>Drafts approved as-is without modifications</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="text-3xl font-bold mb-2 text-green-600 dark:text-green-400">
                                        {contextSwitching.draftsApprovedWithoutInstructions.toLocaleString()}
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        {contextSwitching.escalationsWithDraft > 0
                                            ? Math.round((contextSwitching.draftsApprovedWithoutInstructions / contextSwitching.escalationsWithDraft) * 100)
                                            : 0}% of drafts with prepared context
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-2 italic">
                                        Each draft represents a structured response prepared before human review.
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </section>

                    {/* Confidence & Governance Section */}
                    <section>
                        <h2 className="text-xl font-semibold mb-4">Confidence & Governance Behavior</h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            How confidence levels affect system behavior - demonstrating selective, governed responses
                        </p>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Confidence Distribution</CardTitle>
                                    <CardDescription>Answers by confidence level</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-4">
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-3 w-3 rounded-full bg-green-600"></div>
                                                    <span className="text-sm font-medium">High (≥80%)</span>
                                                </div>
                                                <span className="text-sm font-bold">{confidence.high.toLocaleString()} ({Math.round((confidence.high / overview.totalQuestions) * 100)}%)</span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-3">
                                                <div
                                                    className="bg-green-600 h-3 rounded-full"
                                                    style={{ width: `${(confidence.high / overview.totalQuestions) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-3 w-3 rounded-full bg-yellow-600"></div>
                                                    <span className="text-sm font-medium">Medium (60-79%)</span>
                                                </div>
                                                <span className="text-sm font-bold">{confidence.medium.toLocaleString()} ({Math.round((confidence.medium / overview.totalQuestions) * 100)}%)</span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-3">
                                                <div
                                                    className="bg-yellow-600 h-3 rounded-full"
                                                    style={{ width: `${(confidence.medium / overview.totalQuestions) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-3 w-3 rounded-full bg-orange-600"></div>
                                                    <span className="text-sm font-medium">Low (&lt;60%)</span>
                                                </div>
                                                <span className="text-sm font-bold">{confidence.low.toLocaleString()} ({Math.round((confidence.low / overview.totalQuestions) * 100)}%)</span>
                                            </div>
                                            <div className="w-full bg-muted rounded-full h-3">
                                                <div
                                                    className="bg-orange-600 h-3 rounded-full"
                                                    style={{ width: `${(confidence.low / overview.totalQuestions) * 100}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card>
                                <CardHeader>
                                    <CardTitle>Guardrails Triggered</CardTitle>
                                    <CardDescription>System guardrails that prevented or escalated responses</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="flex items-center justify-between p-4 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-800">
                                        <div className="flex items-center gap-3">
                                            <Shield className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                                            <div>
                                                <p className="text-sm font-medium">Guardrails Triggered</p>
                                                <p className="text-xs text-muted-foreground">Total guardrail activations</p>
                                            </div>
                                        </div>
                                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                            {guardrails.triggered.toLocaleString()}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between p-4 rounded-lg bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800">
                                        <div className="flex items-center gap-3">
                                            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                                            <div>
                                                <p className="text-sm font-medium">Answers Blocked</p>
                                                <p className="text-xs text-muted-foreground">Prevented by guardrails</p>
                                            </div>
                                        </div>
                                        <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                                            {guardrails.blockedAnswers.toLocaleString()}
                                        </div>
                                    </div>

                                    <div className="flex items-center justify-between p-4 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-800">
                                        <div className="flex items-center gap-3">
                                            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                                            <div>
                                                <p className="text-sm font-medium">Escalated Due to Guardrails</p>
                                                <p className="text-xs text-muted-foreground">Sent for human review</p>
                                            </div>
                                        </div>
                                        <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
                                            {guardrails.escalatedDueToGuardrails.toLocaleString()}
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </section>

                    {/* Trends Over Time Section */}
                    <section>
                        <h2 className="text-xl font-semibold mb-4">Trends Over Time</h2>
                        <p className="text-sm text-muted-foreground mb-4">
                            Weekly patterns showing question volume and resolution behavior
                        </p>
                        <Card>
                            <CardHeader>
                                <CardTitle>Questions Per Week</CardTitle>
                                <CardDescription>Total questions, auto-resolved, and escalated over time</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {trends.weeklyQuestions.map((week, index) => (
                                        <div key={index} className="space-y-2">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-medium">{week.week}</span>
                                                <span className="text-sm text-muted-foreground">
                                                    Total: {week.total} | Auto: {week.autoResolved} | Escalated: {week.escalated}
                                                </span>
                                            </div>
                                            <div className="relative w-full bg-muted rounded-full h-8 overflow-hidden">
                                                {/* Auto-resolved bar (green) */}
                                                <div
                                                    className="absolute left-0 top-0 h-full bg-green-600 flex items-center justify-end pr-2"
                                                    style={{ width: `${(week.autoResolved / maxTrendValue) * 100}%` }}
                                                >
                                                    {week.autoResolved > 0 && (
                                                        <span className="text-xs font-medium text-white">{week.autoResolved}</span>
                                                    )}
                                                </div>
                                                {/* Escalated bar (yellow) */}
                                                <div
                                                    className="absolute h-full bg-yellow-600 flex items-center justify-end pr-2"
                                                    style={{
                                                        left: `${(week.autoResolved / maxTrendValue) * 100}%`,
                                                        width: `${(week.escalated / maxTrendValue) * 100}%`,
                                                    }}
                                                >
                                                    {week.escalated > 0 && (
                                                        <span className="text-xs font-medium text-white">{week.escalated}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-6 flex items-center gap-4 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                        <div className="h-3 w-3 rounded bg-green-600"></div>
                                        <span>Auto-Resolved</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="h-3 w-3 rounded bg-yellow-600"></div>
                                        <span>Escalated</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </section>
                </div>
            </div>
        </div>
    );
};

